import Responsive from "@components/Responsive";
import Link from "next/link";
import NavBar from "@/pagecomponents/newcomponents/Navbar";
import Footer from "@/pagecomponents/newcomponents/Footer";
import SEOTags from "@/components/SEOTags";

const openCookieSettings = () => {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new Event("lifeshack:open-cookie-settings"));
  }
};

export default function CookiePolicyPage() {
  return (
    <>
      <SEOTags
        title="Cookie Policy | LifeShack"
        description="Learn how LifeShack uses essential, analytics, functional, affiliate, and advertising technologies and how to manage your choices."
        url="https://www.lifeshack.com/cookies/"
      />
      <NavBar secondary />
      <main style={{ padding: "24px", maxWidth: "1000px", margin: "0 auto", lineHeight: 1.6 }}>
        <h1>LifeShack Cookie Policy</h1>
        <p><strong>Effective and last updated: August 20, 2026</strong></p>

        <p>
          This Cookie Policy explains how LifeShack Inc. uses cookies, pixels,
          local storage, SDKs, and similar technologies on lifeshack.com and
          related services. It supplements our <Link href="/privacy/">Privacy Policy</Link>.
        </p>

        <hr />

        <h2>1. What These Technologies Do</h2>
        <p>
          Cookies are small files stored by your browser. Local storage keeps
          information in your browser, while pixels and SDKs can send events to
          us or a provider. Some expire when you close your browser; others
          remain until their configured expiration or until you delete them.
        </p>

        <h2>2. Categories We Use</h2>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th style={cellStyle}>Category</th>
                <th style={cellStyle}>What it does</th>
                <th style={cellStyle}>Examples</th>
                <th style={cellStyle}>Choice</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td style={cellStyle}><strong>Essential</strong></td>
                <td style={cellStyle}>Authentication, security, session continuity, billing flow, load balancing, and recording your privacy choices.</td>
                <td style={cellStyle}>LifeShack, Amazon Web Services/Cognito, Stripe, and the <code>lifeshack_cookie_consent</code> preference.</td>
                <td style={cellStyle}>Required for the requested Service; cannot be disabled through Cookie Settings.</td>
              </tr>
              <tr>
                <td style={cellStyle}><strong>Analytics</strong></td>
                <td style={cellStyle}>Measure pages and features used, diagnose errors, understand performance, and improve the Service. Session replay is disabled on sensitive account and admin routes and configured to mask inputs and page text.</td>
                <td style={cellStyle}>PostHog, Google Analytics, Hotjar, and Heap when enabled.</td>
                <td style={cellStyle}>Off until accepted; may be disabled at any time.</td>
              </tr>
              <tr>
                <td style={cellStyle}><strong>Marketing and attribution</strong></td>
                <td style={cellStyle}>Attribute referrals, measure advertising conversions, and understand campaign performance.</td>
                <td style={cellStyle}>Google Ads, OpenAI Ads, and Rewardful when enabled.</td>
                <td style={cellStyle}>Off until accepted; may be disabled at any time. Recognized opt-out preference signals are honored where required.</td>
              </tr>
              <tr>
                <td style={cellStyle}><strong>Functional</strong></td>
                <td style={cellStyle}>Remember optional interface, language, and personalization choices that are not strictly necessary.</td>
                <td style={cellStyle}>LifeShack preference storage and optional interface integrations.</td>
                <td style={cellStyle}>Off until accepted; may be disabled at any time.</td>
              </tr>
            </tbody>
          </table>
        </div>

        <h2>3. Current Providers and Duration</h2>
        <p>
          The exact names and lifetimes of provider cookies can change as
          providers update their services. Essential authentication items
          generally last for a session or the period needed to keep you signed
          in. Analytics and advertising identifiers may last from a session to
          approximately two years, subject to provider configuration and your
          choices. Provider technologies are governed by the provider&apos;s own
          privacy terms as well as our agreements with that provider.
        </p>

        <h2>4. Manage or Withdraw Your Choice</h2>
        <p>
          You can accept all optional categories, accept only necessary
          technologies, or make category-level choices. Withdrawing consent is
          as easy as giving it: open Cookie Settings below at any time. Your
          choice applies to the current browser or device. Browser settings can
          also block or delete stored items, although blocking essential items
          may prevent parts of the Service from working.
        </p>
        <p>
          <button type="button" onClick={openCookieSettings} style={buttonStyle}>
            Open Cookie Settings
          </button>
        </p>

        <p>
          Where applicable, LifeShack treats a recognized Global Privacy Control
          signal as a request to disable marketing and advertising sharing for
          that browser. “Do Not Track” is a separate signal without a uniform
          industry standard and may not change how the Service operates.
        </p>

        <h2>5. Changes and Contact</h2>
        <p>
          We may update this Policy when our technologies, providers, or legal
          obligations change. We will revise the date above and provide
          additional notice where required. Questions can be sent to{" "}
          <a href="mailto:privacy@lifeshack.com">privacy@lifeshack.com</a>.
        </p>
      </main>
      <Footer />
    </>
  );
}

const cellStyle = {
  border: "1px solid #ddd",
  padding: ".65rem",
  textAlign: "left" as const,
  verticalAlign: "top" as const,
};

const buttonStyle = {
  background: "#4B2AAD",
  border: 0,
  borderRadius: "8px",
  color: "white",
  cursor: "pointer",
  fontWeight: 700,
  padding: "12px 18px",
};

CookiePolicyPage.getLayout = (page) => (
  <Responsive>
    {page}
  </Responsive>
);
